import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import optimizationsApi from 'api/optimizations';
import {
  buildKanbanGroupCardFromApiGroup,
  normalizeGroupsResponse,
} from 'utils/optimizationTransformers';

const PENDING_STATUSES = new Set(['NEW', 'SHOWN']);

function getChildItemCount(groupCard) {
  const child = Array.isArray(groupCard?.childCards) ? groupCard.childCards : [];
  return child.reduce((sum, c) => {
    const campaignGroups = Array.isArray(c?.campaign_groups) ? c.campaign_groups : [];
    const items = campaignGroups[0]?.items;
    const n = Array.isArray(items) ? items.length : 0;
    return sum + n;
  }, 0);
}

function normalizeOptimizationType(type) {
  if (!type) return 'OPTIMIZAR';
  const s = String(type).trim().toUpperCase();
  if (s.includes('PAUS')) return 'PAUSAR';
  if (s.includes('ESCAL')) return 'ESCALAR';
  if (s.includes('OPTIM')) return 'OPTIMIZAR';
  return s;
}

/**
 * Home - pending optimizations summary.
 * Uses GET /api/get-active-insights and filters:
 * - status NEW | SHOWN
 * - is_liked !== false
 */
export default function useHomePendingOptimizations({ enabled }) {
  const query = useQuery({
    queryKey: ['home', 'pending-optimizations'],
    enabled: Boolean(enabled),
    queryFn: async () => {
      const res = await optimizationsApi.getActiveOptimizations();
      const { groups } = normalizeGroupsResponse(res?.data);

      const filteredGroups = (Array.isArray(groups) ? groups : [])
        .map((g) => {
          const suggestions = Array.isArray(g?.suggestions) ? g.suggestions : [];
          const filteredSuggestions = suggestions.filter((s) => {
            const st = String(s?.status || '').toUpperCase();
            const isPending = PENDING_STATUSES.has(st);
            const isVisible = s?.is_liked !== false;
            return isPending && isVisible;
          });
          return { ...g, suggestions: filteredSuggestions };
        })
        .filter((g) => Array.isArray(g?.suggestions) && g.suggestions.length > 0);

      return filteredGroups
        .map((g, idx) => buildKanbanGroupCardFromApiGroup(g, null, idx))
        .filter(Boolean);
    },
    staleTime: 2 * 60 * 1000,
    gcTime: 20 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const summary = useMemo(() => {
    const groupCards = query.data ?? [];
    const withCounts = (Array.isArray(groupCards) ? groupCards : []).map((c) => ({
      card: c,
      itemsCount: getChildItemCount(c),
      type: normalizeOptimizationType(c?.type),
    }));

    const uniqueTypes = new Set(withCounts.map((x) => x.type).filter(Boolean));

    const top3 = [...withCounts]
      .sort((a, b) => (b.itemsCount || 0) - (a.itemsCount || 0))
      .slice(0, 3);

    return { totalTypes: uniqueTypes.size, top3 };
  }, [query.data]);

  return {
    groupCards: query.data ?? [],
    loading: query.isPending,
    error:
      query.error?.response?.data?.error ||
      query.error?.response?.data?.message ||
      query.error?.message ||
      (query.isError ? 'No se pudieron cargar las optimizaciones pendientes.' : null),
    summary,
    refetch: query.refetch,
  };
}

